import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { tr } from '@util/tr';
import { toast } from '@web/util/toast';
import { observeNoLoading } from '@web/util/loading/loading';
import { WSPaymentsService } from 'services/dws/ws-payments.service';
import { ReservationDetailService } from '@services/dws/scheduling/reservation-detail/reservation-detail.service';
import { Observable } from 'rxjs';

export interface PaymentsRefundDialogData {
  wineryId: string;
  paymentIntentId: string;
  amountCents: number;
  maxAmountCents: number;
  currency: string;
  customerName?: string;
  /** When set, refund goes through CRM so reservation refund state persists on reopen. */
  reservationId?: string;
}

export interface PaymentsRefundDialogResult {
  confirmed: boolean;
  amountCents?: number;
  reasonCode?: string;
  notes?: string;
}

interface SelectOption {
  id: string;
  name: string;
}

@Component({
  selector: 'web-payments-refund-dialog',
  templateUrl: './payments-refund-dialog.component.html',
  styleUrls: ['./payments-refund-dialog.component.scss'],
})
export class PaymentsRefundDialogComponent {
  form: FormGroup;
  isProcessing = false;

  readonly reasonOptions: SelectOption[] = [
    { id: 'requested_by_customer', name: tr('payments.list.refundReasonRequestedByCustomer') },
    { id: 'duplicate', name: tr('payments.list.refundReasonDuplicate') },
    { id: 'fraudulent', name: tr('payments.list.refundReasonFraudulent') },
  ];

  readonly optionNameFormatter = (item: SelectOption | null): string => item?.name ?? '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentsRefundDialogData,
    private dlg: MatDialogRef<PaymentsRefundDialogComponent>,
    private wsPaymentsService: WSPaymentsService,
    private reservationDetailService: ReservationDetailService,
    fb: FormBuilder,
  ) {
    const defaultAmount = this.centsToMajor(data.maxAmountCents);
    this.form = fb.group({
      amount: [defaultAmount, [Validators.required, Validators.min(0.01)]],
      reason: [this.reasonOptions[0], Validators.required],
      notes: [''],
    });
  }

  get currencySymbol(): string {
    const c = (this.data.currency || 'eur').toUpperCase();
    if (c === 'EUR') return '€';
    return c;
  }

  get maxAmountLabel(): string {
    return `${this.centsToMajor(this.data.maxAmountCents).toFixed(2)} ${this.currencySymbol}`;
  }

  closeClicked(): void {
    if (this.isProcessing) return;
    this.dlg.close({ confirmed: false });
  }

  cancelClicked(): void {
    this.closeClicked();
  }

  confirmClicked(): void {
    if (this.isProcessing) return;

    const amountMajor = this.parseAmountMajor(this.form.get('amount')?.value);
    if (amountMajor == null || amountMajor <= 0) {
      this.form.get('amount')?.markAsTouched();
      this.form.get('amount')?.setErrors({ invalid: true });
      return;
    }

    const amountCents = Math.round(amountMajor * 100);
    if (amountCents > this.data.maxAmountCents) {
      this.form.get('amount')?.markAsTouched();
      this.form.get('amount')?.setErrors({ max: true });
      return;
    }

    const reasonOption = this.form.get('reason')?.value as SelectOption | null;
    const notes = this.asTrimmedString(this.form.get('notes')?.value);
    const reasonCode = reasonOption?.id || 'requested_by_customer';
    const partialAmountCents = amountCents < this.data.maxAmountCents ? amountCents : undefined;

    this.isProcessing = true;
    const refundRequest: Observable<unknown> = this.data.reservationId
      ? this.reservationDetailService.refundReservationV2(this.data.reservationId, partialAmountCents)
      : this.wsPaymentsService.refundDwspayPaymentIntent(
        this.data.wineryId,
        this.data.paymentIntentId,
        { reason: reasonCode, notes, amountCents: partialAmountCents },
      );

    observeNoLoading(refundRequest).subscribe({
      next: () => {
        this.dlg.close({
          confirmed: true,
          amountCents,
          reasonCode,
          notes,
        });
      },
      error: (err) => {
        console.error('Error refunding payment:', err);
        toast(tr('payments.list.refundError'));
        this.isProcessing = false;
      },
    });
  }

  onReasonChange(value: SelectOption | null): void {
    this.form.get('reason')?.setValue(value);
    this.form.get('reason')?.markAsDirty();
  }

  private centsToMajor(cents: number): number {
    return Math.round(cents) / 100;
  }

  private parseAmountMajor(value: unknown): number | null {
    if (value == null || value === '') return null;
    const normalized = String(value).trim().replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private asTrimmedString(value: unknown): string | undefined {
    if (value == null) return undefined;
    const trimmed = String(value).trim();
    return trimmed || undefined;
  }
}
